const UNITS: [number, string][] = [
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
];

/**
 * Compact "xx.x" form with a k/m/b scale suffix (e.g. 23990000 -> "23.9 M").
 * Truncates rather than rounds so a value never displays as crossing a
 * threshold it hasn't actually reached (23.99 M shows as "23.9 M", not "24.0 M").
 */
export function formatCompactNumber(value: number): string {
    const floored = Math.floor(value);
    if (floored < 1000) return floored.toString();

    for (const [divisor, suffix] of UNITS) {
        if (floored >= divisor) {
            const scaled = floored / divisor;
            const decimals = scaled >= 100 ? 0 : scaled >= 10 ? 1 : 2;
            const factor = 10 ** decimals;
            // Epsilon absorbs float noise (e.g. 1.98 * 100 === 197.99999999999997)
            // without meaningfully affecting the truncation itself.
            const truncated = Math.floor(scaled * factor + 1e-9) / factor;
            return `${truncated.toFixed(decimals)} ${suffix}`;
        }
    }
    return floored.toString();
}

/** Exact value with thousands separators, for hover tooltips. */
export function formatFullNumber(value: number): string {
    return Math.floor(value).toLocaleString();
}

/** `h:mm:ss` when the duration is at least an hour, otherwise `mm:ss`. */
export function formatCountdown(ms: number): string {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1_000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Same formatting as {@link formatCountdown}, from a seconds duration instead of milliseconds. */
export function formatDuration(seconds: number): string {
    return formatCountdown(seconds * 1_000);
}
